import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { User } from '#models/user';
import { LoadingIndicatorService } from '#services/shared/loading-indicator.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { UserService, ObsService } from '#services/api';
import { finalize, map } from 'rxjs';
import { AuthService } from '#services/shared';
import { EmailService } from '#services/api/email.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '#environments/environment';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false
})
export class UsersComponent implements OnInit {
  public users: User[];
  selectedPermissions: string[] = [];
  permissionNumber: number;
  directoryUsers: User[];
  CAI: string = '';
  FirstName: string = '';
  LastName: string = '';
  EmailAddress: string = '';
  userInput: string = '';
  selectedUser: User;
  activeUsers: User[];
  inactiveUsers: User[];
  activeUsersToFilter: User[];
  inactiveUsersToFilter: User[];    
  isAdmin: boolean = false;
  isNew3rdParty: boolean = false;
  readyToSave: boolean = false;
  obses: any[];
  selectedObs: any;
  dropdownOptions = [
    { name: 'All users'},
    { name: 'System Admins'},
    { name: 'Internal users'},
    { name: 'Contractors'}
  ]; 
  selectedAUOption = { name: 'All users'}; 
  selectedDUOption = { name: 'All users'}; 
  selectedRoleFilter: string;
  firstPage = 0;

  constructor(
    public loadingIndicatorSvc: LoadingIndicatorService,
    private messageSvc: MessageService,
    private emailSvc: EmailService,
    private userSvc: UserService,
    private authSvc: AuthService,
    private confirmationSvc: ConfirmationService,
    private obsSvc: ObsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.isAdmin = this.userSvc.isSystemAdmin();
  }

  ngOnInit() {
    this.getActiveUsers();
    this.getInActiveUsers();
  }

  private showError(e, message: string) {
    this.messageSvc.add({
      severity: 'error',
      summary: message,
      sticky: true,
      detail: e.error.Message || ''
    });
  }

  refreshObses() {
    this.obsSvc.getObsTimezones().subscribe(data => {
      this.obses = data;
    });
  }

  clearObsSelection() {
    this.selectedObs = '';
  }

  /**
   * Populates user table
   */
  getActiveUsers() {
    this.loadingIndicatorSvc.show();
    this.userSvc.getAll()
    .pipe(map(users => users.filter(user=>!user.IsDeleted)))
    .subscribe({
      next:(data) => {      
        this.activeUsers = data.map(user => {
          let newUser = Object.assign(new User(), user); // Convert to User instance
          newUser.IsActive = !user.IsDeleted; // Set computed property
          return newUser;
        });        
        this.activeUsersToFilter = [...this.activeUsers];   
        let i: number;
        for (i = 0; i < this.activeUsers.length; i++) {
          this.adminCheck(this.activeUsers[i]);
        }
        this.loadingIndicatorSvc.hide();
        // activeUsers feeds the p-table [value] binding; stabilize the view
        // synchronously to avoid NG0100.
        this.cdr.detectChanges();
      },
      error:(e) => {
        this.activeUsers = [];
        this.showError(e, this.translate.instant('USERS.FailedUserList'));
      }
  });
}

getInActiveUsers() {
  this.loadingIndicatorSvc.show();
  this.userSvc.getAll()
  .pipe(map(users => users.filter(user=> user.IsDeleted)))
  .subscribe({
    next:(data) => {
      this.inactiveUsers = data.map(user => {
        let newUser = Object.assign(new User(), user); // Convert to User instance
        newUser.IsActive = !user.IsDeleted; // Set computed property
        return newUser;
      });
      this.inactiveUsersToFilter = [...this.inactiveUsers];         
      let i: number;
      for (i = 0; i < this.inactiveUsers.length; i++) {
        this.adminCheck(this.inactiveUsers[i]);
      }
      this.loadingIndicatorSvc.hide();
      // inactiveUsers feeds the p-table [value] binding; stabilize the view
      // synchronously to avoid NG0100.
      this.cdr.detectChanges();
    },
    error:(e) => {
      this.inactiveUsers = [];
      this.showError(e, this.translate.instant('USERS.FailedUserList'));
    }
});
}

  /*
   * Checks to see if the user is a System Admin or not
   */
  adminCheck(user: User) {
    if (user.Permissions > 258) {
      user.sysAdmin = true;
    } else {
      user.sysAdmin = false;
    }
  }

  /**
   * Seaches for users in Azure Directory after every keystroke in search field
   * @param event Keystroke in the search field
   */
  searchDirectoryUsers(event) {
    this.userSvc.getUserFromDirectory(event.query).subscribe({
      next:(data: User[]) => {
        this.directoryUsers = data;
      },
      error:(e) => {
        this.directoryUsers = [];
        this.showError(e, this.translate.instant('USERS.FailedDirectoryUsers'));
      }
  });
  }

  filterActiveUsersonRoleSelect(event: any){
    this.activeUsers = this.filterUsersonRoleSelect(event, this.activeUsersToFilter);   
  }

  filterInActiveUsersonRoleSelect(event: any) {
    this.inactiveUsers = this.filterUsersonRoleSelect(event, this.inactiveUsersToFilter);
  }
  
  filterUsersonRoleSelect(event: any, usersToFilter: User[]): User[] {
    this.selectedRoleFilter = event.value.name;    
    this.firstPage = 0;  
    const allowedDomains = environment.internalDomains;

    switch(this.selectedRoleFilter){
      case 'Internal users':
        return usersToFilter.filter(
          user => allowedDomains.some(domain=> user.Email.endsWith(domain))
        );

      case 'Contractors':
        return usersToFilter.filter(
          user => !allowedDomains.some(domain=> user.Email.endsWith(domain))
        );

      case 'System Admins':            
        return usersToFilter.filter(
          user => user.sysAdmin == true);

      case 'All users':        
        return usersToFilter;

      default:
        return [];
    }    
  } 

  /**
   * Auto-completes the rest of the form after a user from the dropdown is selected
   * @param event Dropdown selection
   */
  fillInfo(event) {    
    this.selectedUser = event.value;
    this.FirstName = this.selectedUser.FirstName;
    this.LastName = this.selectedUser.LastName;
    this.EmailAddress = this.selectedUser.Email;
    this.readyToSave = true;
  }

  populateFromForm() {
    this.selectedUser = new User();
    this.selectedUser.FirstName = this.FirstName;
    this.selectedUser.LastName = this.LastName;
    this.selectedUser.Email = this.EmailAddress;

    this.readyToSave = true;
  }
  /**
   * Empties user's autofill info
   */
  emptyInfo() {
    this.FirstName = '';
    this.LastName = '';
    this.EmailAddress = '';
    this.readyToSave = false;
    this.selectedUser = new User();
    this.selectedObs = '';
  }
   /** Validate User  */
 
   
userExistsInUserList(a: User) {
  return this.activeUsers.some(user => user.Email === a.Email)
}
     


  /**
   * Saves the user created to the database
   * Checks to ensure that user data has been filled out
   */
  saveUser(): void {
    this.loadingIndicatorSvc.show();

    if(this.isNew3rdParty){
      this.populateFromForm();
    }

    if ((this.selectedUser === undefined) || (this.selectedUser.Email.length <= 0 ) || (this.selectedUser.FirstName.length <= 0) || (this.selectedUser.LastName.length <= 0)) {
      this.messageSvc.add({
        severity: 'error',
        sticky: true,
        summary: this.translate.instant('USERS.MissingUserInformation'),
        detail: this.translate.instant('USERS.FillOutUser')
      });
      this.loadingIndicatorSvc.hide();
      return;
    }

    if (!this.readyToSave) {
      this.messageSvc.add({
        severity: 'error',
        sticky: true,
        summary: this.translate.instant('USERS.NoUserToSave'),
        detail: this.translate.instant('USERS.FillOutUser')
      });
      this.loadingIndicatorSvc.hide();
      return;
    }
    
   const isNotValid=this.userExistsInUserList(this.selectedUser)
   if (isNotValid)
    {
      this.loadingIndicatorSvc.hide();
      this.messageSvc.add({ severity: 'error', summary: this.translate.instant('USERS.ValidateUser'), detail: this.translate.instant('USERS.ValidateUser') });
      this.emptyInfo();
      this.getActiveUsers();
    }
   else if (!isNotValid)
    {
    this.selectedUser.Permissions = 3;
    this.userSvc
      .addUser(this.selectedUser).pipe(
      finalize(() => {
        this.loadingIndicatorSvc.hide();
        if(this.isNew3rdParty){
          this.sendEmailNotification()
        }
        this.emptyInfo();
      }))
      .subscribe({
        next:() => {
          this.messageSvc.add({ severity: 'success', summary: this.translate.instant('USERS.UserAdded'), detail: this.translate.instant('USERS.UserSaved') });
          this.getActiveUsers();
        },
        error:(e) => {
          this.showError(e, this.translate.instant('USERS.FailedNewUser'));
        }
       } );
      }
      this.loadingIndicatorSvc.hide();
  }

  sendEmailNotification(): void {
    this.loadingIndicatorSvc.show();


      this.emailSvc.newUserNotification(this.selectedUser).pipe(
      finalize(() => {
        this.loadingIndicatorSvc.hide();
      }))
      .subscribe({
        next:() => {
          this.messageSvc.add({ severity: 'success', summary: this.translate.instant('USERS.Notification'), detail: this.translate.instant('USERS.NotificationSent') });
        },
        error:(e) => {
          this.showError(e, this.translate.instant('USERS.FailedNotification'));
        }
  });

  }

  /**
   * Update user's admin rights, reverts changes if confirmation is not accepted
   * @param user the user who is being updated
   */
  updateUser(user: User): void {
    this.confirmationSvc.confirm({
      header: this.translate.instant('USERS.PleaseConfirm'),
      message: this.translate.instant('USERS.ConfirmAdmin') + `${user.FirstName + ' ' + user.LastName}?`,
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
      accept: () => {
        this.loadingIndicatorSvc.show();
        if (user.sysAdmin) {
          user.Permissions = 256;
        } else {
          user.Permissions = 1;
        }
        this.userSvc
          .updateUser(user).pipe(
          finalize(() => this.loadingIndicatorSvc.hide()))
          .subscribe({
            next:(data: any) => {
              user = data;
            },
            error:(e) => this.showError(e, this.translate.instant('USERS.FailedUpdate'))
      });
      },
      reject: () => {
        user.sysAdmin = !user.sysAdmin;
      }
    });
  }

  activeInActiveUserstabChanged(event: any) {    
    this.firstPage = 0;
  }
 
  toggleUserStatus(user: User, newIsActiveValue: boolean) {
    const previousState = !newIsActiveValue; // Store the original state before change
  
    this.confirmationSvc.confirm({
      header: this.translate.instant('USERS.PleaseConfirm'),
      message: newIsActiveValue 
        ? this.translate.instant('USERS.ConfirmUserEnable') + `${user.FirstName + ' ' + user.LastName}?`
        : this.translate.instant('USERS.ConfirmUserDisable') + `${user.FirstName + ' ' + user.LastName}?`,
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
  
      accept: () => {
        this.loadingIndicatorSvc.show();  
        user.IsActive = newIsActiveValue; // Apply the confirmed value
        user.IsDeleted = !newIsActiveValue; // Ensure IsDeleted reflects the correct state
  
        this.userSvc.updateUser(user)
          .pipe(finalize(() => this.loadingIndicatorSvc.hide()))
          .subscribe({
            next: () => {
              user.IsDeleted                
                ? this.messageSvc.add({ severity: 'success', summary: this.translate.instant('USERS.UserDisabled') })
                : this.messageSvc.add({ severity: 'success', summary: this.translate.instant('USERS.UserEnabled') });
  
              this.getActiveUsers();
              this.getInActiveUsers();
            },
            error: (e) => {
              this.showError(e, this.translate.instant('USERS.FailedDisabled'));
              user.IsActive = previousState; // Revert on error
              user.IsDeleted = !user.IsActive; // Ensure consistency
            }
          });
      },  
      reject: () => {        
        user.IsActive = previousState; // Revert if user clicks "No"
        user.IsDeleted = !user.IsActive; // Ensure consistency
      }
    });
  }  

  toggleUserType() {
    this.emptyInfo();
  }
}
